import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { authApi, meKey } from '@/api/auth'
import { isApiError } from '@/api/client'
import { AppShell } from '@/app/AppShell'
import { NotFound } from '@/app/NotFound'

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
})

const str = (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined)
const bool = (v: unknown) => (v === true || v === 'true' || v === '1' ? true : undefined)
const num = (v: unknown) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  return Number.isFinite(n) && n > 0 ? n : undefined
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => ({ redirect: str(s.redirect) }),
  component: lazyRouteComponent(() => import('@/features/auth/LoginPage'), 'LoginPage'),
})

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: lazyRouteComponent(() => import('@/features/auth/InvitePage'), 'InvitePage'),
})

/** Authenticated layout: resolves the current user or redirects to /login. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData({ queryKey: meKey, queryFn: authApi.me, staleTime: 60_000 })
    } catch (e) {
      if (isApiError(e) && e.isUnauthorized) {
        throw redirect({ to: '/login', search: { redirect: location.href === '/' ? undefined : location.href } })
      }
      throw e
    }
  },
  component: AppShell,
})

/** Server-sort params of the page's tables (`sort`/`dir`, or `<x>sort`/`<x>sortDir` for a second table; see lib/sorting). */
const SORT_PARAM = /^(sort|dir|[a-z]+sort|[a-z]+sortDir)$/
function sortSearch(s: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(s)) if (SORT_PARAM.test(k) && typeof v === 'string' && v !== '') out[k] = v
  return out
}

function page(path: string, loader: () => Promise<Record<string, unknown>>, name: string, validateSearch?: (s: Record<string, unknown>) => Record<string, unknown>) {
  return createRoute({
    getParentRoute: () => appRoute,
    path,
    // Pages that validate their search keep the sort params of their tables too.
    validateSearch: validateSearch && ((s: Record<string, unknown>) => ({ ...validateSearch(s), ...sortSearch(s) })),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: lazyRouteComponent(loader as any, name as any),
  })
}

const tasksSearch = (s: Record<string, unknown>) => ({
  view: str(s.view) as 'list' | 'board' | 'calendar' | 'series' | undefined,
  task: str(s.task),
  q: str(s.q),
  mine: s.mine === true || s.mine === 'true' || s.mine === '1' ? true : undefined,
  status: str(s.status),
  overdue: s.overdue === true || s.overdue === 'true' ? true : undefined,
  month: str(s.month),
})

const routes = [
  page('/', () => import('@/features/today/TodayPage'), 'TodayPage'),
  page('/tasks', () => import('@/features/tasks/TasksPage'), 'TasksPage', tasksSearch),
  page('/schedule', () => import('@/features/schedule/SchedulePage'), 'SchedulePage', (s) => ({
    date: str(s.date),
    mode: str(s.mode),
    by: str(s.by),
    doctor: str(s.doctor),
    room: str(s.room),
  })),
  page('/journeys', () => import('@/features/journeys/JourneysPage'), 'JourneysPage', (s) => ({
    from: str(s.from),
    to: str(s.to),
    state: str(s.state),
    flag: str(s.flag),
    client: str(s.client),
    q: str(s.q),
    review: bool(s.review),
  })),
  page('/journeys/for', () => import('@/features/journeys/JourneyForPage'), 'JourneyForPage', (s) => ({ visit: str(s.visit), appointment: str(s.appointment), call: str(s.call) })),
  page('/journeys/review', () => import('@/features/journeys/LinkReviewPage'), 'LinkReviewPage', (s) => ({ type: str(s.type) })),
  page('/journeys/$journeyId', () => import('@/features/journeys/JourneyDetailPage'), 'JourneyDetailPage'),
  page('/clients', () => import('@/features/clients/ClientsPage'), 'ClientsPage', (s) => ({ q: str(s.q) })),
  page('/clients/$clientId', () => import('@/features/clients/ClientPage'), 'ClientPage'),
  page('/pets/$petId', () => import('@/features/clients/PetPage'), 'PetPage'),
  page('/doctor', () => import('@/features/doctor/DoctorPage'), 'DoctorPage'),
  page('/doctor/drafts/$draftId', () => import('@/features/doctor/RecommendationPage'), 'DraftRedirectPage'),
  page('/doctor/visits/$visitId', () => import('@/features/doctor/RecommendationPage'), 'RecommendationPage'),
  page('/sessions/$sessionId', () => import('@/features/calls/SessionPage'), 'SessionPage'),
  page('/calls', () => import('@/features/calls/CallsPage'), 'CallsPage'),
  page('/calls/missed', () => import('@/features/calls/MissedPage'), 'MissedPage'),
  page('/calls/$callId', () => import('@/features/calls/CallDetailPage'), 'CallDetailPage'),
  page('/inbox', () => import('@/features/inbox/InboxPage'), 'InboxPage', (s) => ({ c: str(s.c) })),
  page('/finance', () => import('@/features/finance/FinancePage'), 'FinancePage'),
  page('/inventory', () => import('@/features/inventory/InventoryPage'), 'InventoryPage', (s) => ({
    tab: str(s.tab),
    wh: str(s.wh),
    kind: str(s.kind),
    q: str(s.q),
    neg: bool(s.neg),
    low: bool(s.low),
    zero: bool(s.zero),
    item: str(s.item),
  })),
  page('/inventory/counts/$runId', () => import('@/features/inventory/CountSheetPage'), 'CountSheetPage'),
  page('/process', () => import('@/features/process/ProcessPage'), 'ProcessPage', (s) => ({
    scope: str(s.scope),
    status: str(s.status),
    area: str(s.area),
    overdue: bool(s.overdue),
    done: bool(s.done),
  })),
  page('/process/runs/$runId', () => import('@/features/process/RunPage'), 'RunPage'),
  page('/process/calendar', () => import('@/features/process/CalendarPage'), 'CalendarPage', (s) => ({
    month: str(s.month),
    mode: str(s.mode),
    branch: str(s.branch),
    area: str(s.area),
  })),
  page('/process/templates', () => import('@/features/process/TemplatesPage'), 'TemplatesPage', (s) => ({ tab: str(s.tab), all: bool(s.all) })),
  page('/process/norms', () => import('@/features/process/NormsPage'), 'NormsPage'),
  page('/lab/shipments', () => import('@/features/labship/LabShipmentsPage'), 'LabShipmentsPage', (s) => ({
    tab: str(s.tab),
    lab: str(s.lab),
    branch: str(s.branch),
    q: str(s.q),
    s: str(s.s),
  })),
  page('/lab/labs', () => import('@/features/labship/LabsPage'), 'LabsPage', (s) => ({ tab: str(s.tab), all: bool(s.all) })),
  page('/vrit', () => import('@/features/vrit/VritPage'), 'VritPage', (s) => ({ branch: str(s.branch), stay: str(s.stay) })),
  page('/vrit/handover', () => import('@/features/vrit/HandoverPage'), 'HandoverPage', (s) => ({ branch: str(s.branch) })),
  page('/incidents', () => import('@/features/incidents/IncidentsPage'), 'IncidentsPage', (s) => ({
    status: str(s.status),
    type: str(s.type),
    category: str(s.category),
    branch: str(s.branch),
    overdue: bool(s.overdue),
    mine: bool(s.mine),
    q: str(s.q),
    id: str(s.id),
    tab: str(s.tab),
  })),
  page('/arrivals', () => import('@/features/arrivals/ArrivalsPage'), 'ArrivalsPage', (s) => ({
    tab: str(s.tab),
    id: str(s.id),
    branch: str(s.branch),
    status: str(s.status),
    days: num(s.days),
    open: bool(s.open),
  })),
  page('/inquiries', () => import('@/features/callcenter/InquiriesPage'), 'InquiriesPage', (s) => ({
    tab: str(s.tab),
    id: str(s.id),
    status: str(s.status),
    channel: str(s.channel),
    topic: str(s.topic),
    branch: str(s.branch),
    overdue: bool(s.overdue),
    mine: bool(s.mine),
    unassigned: bool(s.unassigned),
    q: str(s.q),
  })),
  page('/call-quality', () => import('@/features/callquality/CallQualityPage'), 'CallQualityPage', (s) => ({
    tab: str(s.tab),
    from: str(s.from),
    to: str(s.to),
    op: str(s.op),
    call: str(s.call),
    criterion: str(s.criterion),
    critical: bool(s.critical),
    g: str(s.g),
    rs: str(s.rs),
    rr: str(s.rr),
  })),
  page('/call-kpi', () => import('@/features/callquality/KpiPage'), 'KpiPage', (s) => ({ from: str(s.from), to: str(s.to) })),
  page('/blood-bank', () => import('@/features/bloodbank/BloodBankPage'), 'BloodBankPage', (s) => ({
    tab: str(s.tab),
    unit: str(s.unit),
    donor: str(s.donor),
    request: str(s.request),
    month: str(s.month),
    q: str(s.q),
    species: str(s.species),
    product: str(s.product),
    status: str(s.status),
  })),
  page('/supply', () => import('@/features/supply/SupplyPage'), 'SupplyPage', (s) => ({
    tab: str(s.tab),
    branch: str(s.branch),
    q: str(s.q),
    overdue: bool(s.overdue),
    mine: bool(s.mine),
    sup: str(s.sup),
    period: str(s.period),
  })),
  page('/supply/invoices/$invoiceId', () => import('@/features/supply/InvoicePage'), 'InvoicePage'),
  page('/kb', () => import('@/features/kb/KbPage'), 'KbPage', (s) => ({
    tab: str(s.tab),
    cat: str(s.cat),
    q: str(s.q),
    level: str(s.level),
    role: str(s.role),
    status: str(s.status),
    pending: bool(s.pending),
    review: bool(s.review),
    doc: str(s.doc),
  })),
  page('/registers', () => import('@/features/registers/RegistersPage'), 'RegistersPage', (s) => ({
    type: str(s.type),
    tab: str(s.tab),
    branch: str(s.branch),
    status: str(s.status),
    q: str(s.q),
    item: str(s.item),
    expiring: bool(s.expiring),
    missing: bool(s.missing),
    mine: bool(s.mine),
  })),
  page('/registers/walk', () => import('@/features/registers/WalkPage'), 'WalkPage', (s) => ({ item: str(s.item), code: str(s.code) })),
  page('/registers/labels', () => import('@/features/registers/LabelsPage'), 'LabelsPage', (s) => ({ type: str(s.type), branch: str(s.branch), ids: str(s.ids) })),
  page('/staff', () => import('@/features/hr/StaffPage'), 'StaffPage', (s) => ({
    tab: str(s.tab),
    q: str(s.q),
    status: str(s.status),
    branch: str(s.branch),
    role: str(s.role),
    emp: str(s.emp),
    card: str(s.card),
  })),
  page('/staff/briefings', () => import('@/features/hr/BriefingsPage'), 'BriefingsPage', (s) => ({
    area: str(s.area),
    kind: str(s.kind),
    from: str(s.from),
    to: str(s.to),
  })),
  page('/staff/me', () => import('@/features/hr/MyStaffPage'), 'MyStaffPage'),
  page('/staff/tests/$testKey', () => import('@/features/hr/TestPage'), 'TestPage'),
  page('/personnel', () => import('@/features/personnel/PersonnelPage'), 'PersonnelPage', (s) => ({
    tab: str(s.tab),
    employer: str(s.employer),
    status: str(s.status),
    q: str(s.q),
    kind: str(s.kind),
    year: str(s.year),
    view: str(s.view),
    emp: str(s.emp),
    card: str(s.card),
  })),
  page('/cash', () => import('@/features/cashops/CashPage'), 'CashPage', (s) => ({
    tab: str(s.tab),
    month: str(s.month),
    branch: str(s.branch),
    from: str(s.from),
    to: str(s.to),
    shift: str(s.shift),
    discount: str(s.discount),
  })),
  page('/mgmt', () => import('@/features/mgmt/MgmtPage'), 'MgmtPage', (s) => ({
    tab: str(s.tab),
    kind: str(s.kind),
    q: str(s.q),
    meeting: str(s.meeting),
    status: str(s.status),
    mine: bool(s.mine),
    overdue: bool(s.overdue),
    month: str(s.month),
  })),
  page('/marketing/content', () => import('@/features/mgmt/ContentPage'), 'ContentPage', (s) => ({
    tab: str(s.tab),
    article: str(s.article),
    fundraiser: str(s.fundraiser),
    q: str(s.q),
    mine: bool(s.mine),
    overdue: bool(s.overdue),
    month: str(s.month),
  })),
  page('/consents', () => import('@/features/consents/ConsentsPage'), 'ConsentsPage', (s) => ({
    tab: str(s.tab),
    q: str(s.q),
    status: str(s.status),
    tpl: str(s.tpl),
    pet: str(s.pet),
    stay: str(s.stay),
    visit: str(s.visit),
    doc: str(s.doc),
  })),
  page('/consents/sign/$documentId', () => import('@/features/consents/SignPage'), 'SignPage'),
  page('/finance/fops', () => import('@/features/finance/FopsPage'), 'FopsPage'),
  page('/registry', () => import('@/features/registry/RegistryPage'), 'RegistryPage', (s) => ({
    tab: str(s.tab),
    q: str(s.q),
    kind: str(s.kind),
    multi: bool(s.multi),
    entity: str(s.entity),
  })),
  page('/finance/checkbox', () => import('@/features/checkbox/CheckboxPage'), 'CheckboxPage', (s) => ({
    tab: str(s.tab),
    from: str(s.from),
    to: str(s.to),
    register: str(s.register),
    fop: str(s.fop),
    type: str(s.type),
    match: str(s.match),
    q: str(s.q),
    r: str(s.r),
  })),
  page('/finance/money', () => import('@/features/money/MoneyPage'), 'MoneyPage', (s) => ({
    tab: str(s.tab),
    from: str(s.from),
    to: str(s.to),
    fop: str(s.fop),
    method: str(s.method),
    status: str(s.status),
    issue: str(s.issue),
    source: str(s.source),
    only: bool(s.only),
    review: bool(s.review),
    kind: str(s.kind),
    q: str(s.q),
    e: str(s.e),
    b: str(s.b),
  })),
  page('/finance/bas', () => import('@/features/bas/BasPage'), 'BasPage', (s) => ({
    tab: str(s.tab),
    month: str(s.month),
    source: str(s.source),
    kind: str(s.kind),
    cat: str(s.cat),
    fop: str(s.fop),
    from: str(s.from),
    to: str(s.to),
    q: str(s.q),
  })),
  page('/fincontrol', () => import('@/features/fincontrol/FincontrolPage'), 'FincontrolPage', (s) => ({
    tab: str(s.tab),
    sev: str(s.sev),
    status: str(s.status),
    cat: str(s.cat),
    fop: str(s.fop),
    period: str(s.period),
    q: str(s.q),
    id: str(s.id),
    year: str(s.year),
  })),
  page('/quality', () => import('@/features/quality/QualityPage'), 'QualityPage'),
  page('/quality/cards', () => import('@/features/clinq/ClinqPage'), 'ClinqPage', (s) => ({
    tab: str(s.tab),
    mine: bool(s.mine),
    doctor: str(s.doctor),
    rule: str(s.rule),
    status: str(s.status),
  })),
  page('/marketing', () => import('@/features/marketing/MarketingPage'), 'MarketingPage'),
  page('/insights', () => import('@/features/insights/InsightsPage'), 'InsightsPage'),
  page('/implementation', () => import('@/features/implementation/ImplementationPage'), 'ImplementationPage', (s) => ({
    phase: str(s.phase),
    status: str(s.status),
    resp: str(s.resp),
    feature: str(s.feature),
    problems: bool(s.problems),
    mine: bool(s.mine),
    stage: str(s.stage),
  })),
  page('/profile', () => import('@/features/profile/ProfilePage'), 'ProfilePage', (s) => ({ tab: str(s.tab) })),
  page('/admin/users', () => import('@/features/admin/UsersPage'), 'UsersPage', (s) => ({ invite: s.invite ? true : undefined })),
  page('/admin/connectors', () => import('@/features/admin/ConnectorsPage'), 'ConnectorsPage'),
  page('/admin/sync', () => import('@/features/admin/SyncPage'), 'SyncPage'),
  page('/admin/jobs', () => import('@/features/admin/JobsPage'), 'JobsPage'),
  page('/admin/rooms', () => import('@/features/admin/RoomsPage'), 'RoomsPage'),
  page('/admin/operators', () => import('@/features/admin/OperatorsPage'), 'OperatorsPage'),
  page('/admin/client-app', () => import('@/features/admin/ClientAppPage'), 'ClientAppPage'),
  page('/admin/settings', () => import('@/features/admin/SettingsPage'), 'SettingsPage'),
  page('/admin/audit', () => import('@/features/admin/AuditPage'), 'AuditPage'),
  page('/admin/system', () => import('@/features/admin/SystemPage'), 'SystemPage'),
]

/** Path pattern of every page inside the app shell (the help-coverage test walks these). */
export const APP_ROUTE_PATHS: string[] = routes.map((r) => (r.options as { path: string }).path)

const routeTree = rootRoute.addChildren([loginRoute, inviteRoute, appRoute.addChildren(routes)])

export function makeRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPendingMs: 150,
    scrollRestoration: true,
  })
}
